import React from 'react';
import {MdClose} from 'react-icons/md';
import {useWindowSize} from 'react-use';
import {
	Box,
	BoxProps,
	Fade,
	Heading,
	HStack,
	IconButton,
	Portal,
	Slide,
} from 'components/ui/chakra';

interface SidePanelProps extends BoxProps {
	children: React.ReactNode;
	title: string;
	isOpen: boolean;
	onClose: () => void;
	size?: 'half' | 'full';
}

const SidePanel = ({children, title, size = 'half', isOpen, onClose, ...rest}: SidePanelProps) => {
	const [numberOfSidePanelsInDom, setNumberOfSidePanelsInDom] = React.useState(
		document.querySelectorAll('.side-panel-container').length,
	);

	const xOffSet = numberOfSidePanelsInDom * 48;

	const {width} = useWindowSize();

	const getSidePanelWidth = () => {
		if (size === 'full' || width < 768) {
			return '100%';
		}

		if (width < 1024) {
			return `${width * 0.8 - xOffSet}px`;
		}

		return `${width * 0.66 - xOffSet}px`;
	};

	React.useEffect(() => {
		setNumberOfSidePanelsInDom(document.querySelectorAll('.side-panel-container').length);
	}, [isOpen]);

	return (
		<Portal>
			<Fade in={isOpen} unmountOnExit>
				<Box
					pos='fixed'
					top={0}
					left={0}
					w='100%'
					h='100%'
					bg='rgba(43, 36, 31, 0.24)'
					onClick={onClose}
				/>
			</Fade>
			<Slide direction='right' in={isOpen} style={{zIndex: 'sticky'}} unmountOnExit>
				<Box
					className='side-panel-container'
					zIndex='sticky'
					pos='fixed'
					right={0}
					top={0}
					bottom={0}
					w={getSidePanelWidth()}
					bg='linear-gradient(180deg, rgba(255, 253, 249, 0.98) 0%, rgba(245, 241, 235, 0.92) 100%)'
					borderLeft='1px solid'
					borderColor='border'
					boxShadow='0 24px 55px rgba(79, 74, 68, 0.14)'
					overflowY='auto'
					{...rest}
				>
					<HStack
						align='center'
						bg='rgba(255, 253, 249, 0.95)'
						borderBottom='1px solid'
						borderColor='border.subtle'
						justify='space-between'
						left={0}
						px={5}
						py={4}
						position='sticky'
						right={0}
						top={0}
						zIndex={1}
					>
						<Heading size='md' textAlign='left'>
							{title}
						</Heading>
						<IconButton
							aria-label='close'
							icon={<MdClose />}
							variant='ghost'
							onClick={onClose}
						/>
					</HStack>
					<Box px={5} py={5} w='full'>
						{children}
					</Box>
				</Box>
			</Slide>
		</Portal>
	);
};

export default SidePanel;
